import DOMPurify from 'dompurify';

// BP 11 — the RENDER-side sanitiser for cached, model-generated explanation HTML.
//
// WHY A SECOND SANITISER
// ----------------------
// `acechem_ai_explanations.html` is already sanitised on write, in
// backend/src/lib/explanationHtml.js, so nothing unsafe reaches the database.
// This is not redundant with that:
//
//   • It is the layer that actually protects the DOM. The backend pass runs in
//     htmlparser2; this one runs in the BROWSER's own parser — the same parser
//     that will build the nodes a moment later. A parser differential between
//     the two (the mXSS class of bug) is exactly the kind of thing that turns a
//     "sanitised" string back into markup on the way in, and only sanitising
//     with the real parser closes it.
//   • Rows may predate the write-side allowlist, or arrive from a database
//     seeded or patched outside the app. Trusting the database because we
//     usually write it is how stored XSS gets shipped.
//
// A slide's own text is part of the prompt that produced this HTML, so a prompt
// injection sitting in imported course material could steer the model into
// emitting a <script> tag or an onerror handler. "We generated it" is not a
// security property. NEVER pass this content to dangerouslySetInnerHTML without
// running it through sanitizeExplanationHtml first — RichExplanation is the only
// component that should be doing either.
//
// SANITISER CHOICE: DOMPurify v3 (~22 KB minified, ~8 KB gzipped, no runtime
// dependencies). It is the de-facto browser sanitiser, is actively maintained
// and security-audited, and — decisively — sanitises through the browser's own
// DOM rather than a bespoke parse of the string. Hand-rolling this is not a
// realistic option: the attack surface is the HTML spec's parsing quirks.
//
// The allowlists below MIRROR backend/src/lib/explanationHtml.js. The two
// packages share no code, so they are duplicated on purpose; each side has its
// own adversarial test (explanationHtml.security.test.tsx here,
// explanationHtml.security.test.mjs there). Change one, change the other.

// Structural HTML the house format uses. Headings start at h3 — the panel that
// renders this already owns the h2 above it, and heading order must not skip.
const ALLOWED_HTML_TAGS = [
  'section', 'div', 'p', 'h3', 'h4', 'h5',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'strong', 'em', 'b', 'i', 'u', 'span', 'small', 'abbr',
  'sub', 'sup', 'code', 'pre', 'br', 'hr', 'blockquote',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'figure', 'figcaption', 'details', 'summary',
];

// The static inline-SVG subset: enough for a reaction diagram or an energy
// profile, and nothing that can fetch, script or animate. Deliberately no
// <use> (xlink:href), no <foreignObject>, no <image>, no <animate>, no <a>.
const ALLOWED_SVG_TAGS = [
  'svg', 'g', 'path', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon',
  'text', 'tspan', 'title', 'desc', 'defs', 'marker',
];

export const ALLOWED_TAGS = [...ALLOWED_HTML_TAGS, ...ALLOWED_SVG_TAGS];

// NO href, NO src, NO style, NO on* — anything not named here is dropped by
// DOMPurify, so event handlers and URL schemes are excluded by construction
// rather than filtered by pattern. `xlink:href` is absent for the same reason.
export const ALLOWED_ATTR = [
  // structural / a11y
  'class', 'role', 'aria-label', 'aria-hidden', 'aria-labelledby', 'id', 'lang', 'title',
  // tables
  'scope', 'colspan', 'rowspan', 'headers', 'span', 'abbr',
  // lists / details
  'start', 'type', 'open',
  // svg geometry + presentation (attribute names are matched case-insensitively)
  'viewbox', 'xmlns', 'width', 'height', 'fill', 'fill-opacity', 'fill-rule',
  'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'stroke-opacity',
  'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'points',
  'transform', 'text-anchor', 'dominant-baseline', 'font-size', 'font-weight', 'font-family',
  'opacity', 'marker-end', 'marker-start', 'orient', 'refx', 'refy',
  'markerwidth', 'markerheight', 'markerunits', 'preserveaspectratio',
];

// The house class vocabulary. Restricting `class` values (DOMPurify allows the
// attribute but not particular values) stops generated content from reaching for
// arbitrary utility classes and reshaping the page around the panel. Every one
// of these is styled against --ws-* tokens in styles/app.css, so it themes in
// all four themes.
export const ALLOWED_CLASSES = new Set([
  'xh',
  'xh-lede',
  'xh-callout',
  'xh-callout--key',
  'xh-callout--warn',
  'xh-callout--note',
  'xh-callout-title',
  'xh-steps',
  'xh-step-why',
  'xh-eq',
  'xh-term',
  'xh-takeaway',
]);

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Belt and braces on top of the allowlist: name the dangerous ones explicitly
  // so a future edit that widens ALLOWED_TAGS cannot quietly readmit them.
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input',
    'button', 'base', 'link', 'meta', 'img', 'a', 'use', 'foreignObject', 'animate', 'set'],
  FORBID_ATTR: ['style', 'href', 'src', 'xlink:href', 'srcset', 'formaction', 'action', 'background', 'ping'],
  // No <html>/<body> wrapper in the output, and no whole-document parse.
  WHOLE_DOCUMENT: false,
  // Keep the SVG namespace working; MathML is not part of the format.
  USE_PROFILES: { html: true, svg: true, svgFilters: false, mathMl: false },
  // Return a string, not a DocumentFragment.
  RETURN_DOM: false as const,
  RETURN_DOM_FRAGMENT: false as const,
  // Refuse anything that would need to escape its container.
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  SAFE_FOR_TEMPLATES: false,
};

/**
 * Sanitise cached explanation HTML for rendering.
 *
 * Total function: any input (null, a number, malformed markup, an attack
 * payload) returns a string that is safe to hand to dangerouslySetInnerHTML.
 * Returns '' for empty input.
 */
export function sanitizeExplanationHtml(html: unknown): string {
  if (html == null) return '';
  const raw = String(html);
  if (!raw.trim()) return '';

  // Restrict `class` to the house vocabulary. DOMPurify allowlists the
  // attribute, not its values, so we filter values in a hook — on the parsed
  // node, before it is serialised, which is the only place the real class list
  // exists. Registered per call and removed straight after so this module never
  // leaves a global hook installed for other DOMPurify callers.
  const filterClasses = (node: Element) => {
    if (!(node instanceof Element) || !node.hasAttribute?.('class')) return;
    const kept = (node.getAttribute('class') || '')
      .split(/\s+/)
      .filter((c) => ALLOWED_CLASSES.has(c));
    if (kept.length) node.setAttribute('class', kept.join(' '));
    else node.removeAttribute('class');
  };

  DOMPurify.addHook('afterSanitizeAttributes', filterClasses as never);
  try {
    return DOMPurify.sanitize(raw, PURIFY_CONFIG) as unknown as string;
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}
